// src/api/axiosConfig.js
// Centralized Axios configuration for all API modules
// Based on FlowAccount API standards

import axios from 'axios';
import { getItem, removeItem } from '../utility/localStorageControl';

/**
 * Base API URLs
 */
export const API_BASE_URL = process.env.VUE_APP_CONNECTIO_API_URL || 'https://api-test.connectio.biz/public';

/**
 * Get x-token from localStorage
 * @returns {string} Token or empty string
 */
export const getToken = () => {
    return getItem('x-token') || getItem('x_token') || '';
};

/**
 * Handle unauthorized response - clear token and redirect to login
 */
export const handleUnauthorized = () => {
    removeItem('x_token');
    removeItem('x-token');

    if (window.location.pathname !== '/auth/login') {
        window.location.href = '/auth/login';
    }
};

/**
 * สร้าง axios instance ที่มี x-token header (Protected API)
 * ใช้สำหรับ API ที่ต้องการ authentication
 * @param {string} baseURL - Base URL (optional, default: API_BASE_URL)
 * @returns {AxiosInstance} Axios instance with x-token header
 */
export const createAuthenticatedClient = (baseURL = API_BASE_URL) => {
    const token = getToken();

    const client = axios.create({
        baseURL: baseURL,
        headers: {
            'Content-Type': 'application/json',
            'x-token': token,
        },
    });

    // Response interceptor to handle 401 (Unauthorized)
    client.interceptors.response.use(
        (response) => {
            // Check code from response body (API returns code in body instead of HTTP status)
            if (response.data && response.data.code === 401) {
                handleUnauthorized();
                return Promise.reject(new Error('Unauthorized. Token not found / Expired'));
            }
            return response;
        },
        (error) => {
            // Check code from response body
            if (error.response && error.response.data && error.response.data.code === 401) {
                handleUnauthorized();
            }
            return Promise.reject(error);
        }
    );

    return client;
};

/**
 * สร้าง axios instance สำหรับ Public API (ไม่ต้องใช้ token)
 * ใช้สำหรับ login, register, และ public endpoints อื่นๆ
 * @param {string} baseURL - Base URL (optional, default: API_BASE_URL)
 * @returns {AxiosInstance} Axios instance without x-token header
 */
export const createPublicClient = (baseURL = API_BASE_URL) => {
    return axios.create({
        baseURL: baseURL,
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

/**
 * Helper function สำหรับจัดการ response
 * ตรวจสอบ code จาก response body (API pattern: { code: 200/0, data: ..., message: ... })
 * @param {Object} response - Axios response object
 * @returns {Object} Standardized response { success, data, message }
 */
export const handleResponse = (response) => {
    // Check code from response body
    // Code 200 or 0 means success
    if (response.data && response.data.code !== undefined && response.data.code !== 200 && response.data.code !== 0) {
        return {
            success: false,
            data: response.data,
            message: response.data.message || `เกิดข้อผิดพลาด (code: ${response.data.code})`,
        };
    }

    // Check if there's nested data (e.g., { success: true, data: [] })
    let data = response.data;
    if (response.data && typeof response.data === 'object' && !Array.isArray(response.data) && 'data' in response.data) {
        data = response.data.data;
    }

    // Return array if it's an array, otherwise keep the data as-is
    const finalData = data;

    return {
        success: true,
        data: finalData,
        message: (response.data && response.data.message) || 'สำเร็จ',
    };
};

/**
 * Helper function สำหรับจัดการ error
 * @param {Error} error - Error object from axios
 * @returns {Object} Standardized error { success, message, data }
 */
export const handleError = (error) => {
    let errorMessage = 'เกิดข้อผิดพลาด';

    if (error.response) {
        // Server responded with error status
        const responseCode = error.response.data && error.response.data.code;
        if (responseCode !== undefined) {
            errorMessage = error.response.data.message ||
                error.response.data.error ||
                `เกิดข้อผิดพลาด (code: ${responseCode})`;
        } else {
            errorMessage = error.response.data.message ||
                error.response.data.error ||
                error.response.statusText ||
                `เกิดข้อผิดพลาด (${error.response.status})`;
        }
    } else if (error.request) {
        // Request made but no response received
        if (error.code === 'ECONNREFUSED') {
            errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ (ECONNREFUSED)';
        } else if (error.code === 'ETIMEDOUT' || error.code === 'ECONNABORTED') {
            errorMessage = 'การเชื่อมต่อหมดเวลา กรุณาลองใหม่อีกครั้ง';
        } else {
            errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
        }
    } else {
        // Something else happened
        errorMessage = error.message || 'เกิดข้อผิดพลาด';
    }

    return {
        success: false,
        message: errorMessage,
        data: error.response ? error.response.data : null,
    };
};

export default {
    API_BASE_URL,
    getToken,
    handleUnauthorized,
    createAuthenticatedClient,
    createPublicClient,
    handleResponse,
    handleError,
};
